/**
 * Content width for text and columns blocks.
 *
 * Bare numbers stay in ch (the old shape). Any other unit is stored as a CSS
 * length string ("800px", "80%"), the same way theme lengths work.
 */

import {
    cssLength,
    lengthNumber,
    lengthUnit,
    storedLength,
} from '../theme/header-modules.js';

export const CONTENT_WIDTH_PREFERRED = 'ch';

/** Units offered on block / page content width. */
export const CONTENT_WIDTH_UNITS = ['ch', 'px', 'em', 'rem', '%', 'vw'];

export const TEXT_WIDTH_RANGES = {
    ch: { min: 30, max: 120, step: 1 },
    px: { min: 240, max: 1600, step: 10 },
    em: { min: 20, max: 80, step: 0.5 },
    rem: { min: 20, max: 80, step: 0.5 },
    '%': { min: 20, max: 100, step: 1 },
    vw: { min: 20, max: 100, step: 0.5 },
};

/** Columns can run wider than a single text measure. */
export const COLUMNS_WIDTH_RANGES = {
    ...TEXT_WIDTH_RANGES,
    ch: { min: 30, max: 180, step: 1 },
    px: { min: 240, max: 2400, step: 10 },
};

export const TEXT_WIDTH_FALLBACK = 65;
export const COLUMNS_WIDTH_FALLBACK = 120;

function unitOf(value) {
    const unit = lengthUnit(value, CONTENT_WIDTH_PREFERRED);
    return CONTENT_WIDTH_UNITS.includes(unit) ? unit : CONTENT_WIDTH_PREFERRED;
}

/** CSS max-width value, defaulting bare numbers to ch. */
export function contentWidthCss(value, fallback = TEXT_WIDTH_FALLBACK) {
    if (value == null || value === '') return cssLength(fallback, CONTENT_WIDTH_PREFERRED);
    return cssLength(value, CONTENT_WIDTH_PREFERRED);
}

/** Human label for change rows ("65ch", "800px"). */
export function formatContentWidth(value, fallback = TEXT_WIDTH_FALLBACK) {
    return contentWidthCss(value, fallback);
}

/**
 * Clamp a stored width into the ranges for its unit. Bare numbers stay bare
 * while the unit is still ch.
 */
export function clampContentWidth(
    value,
    ranges = TEXT_WIDTH_RANGES,
    fallback = TEXT_WIDTH_FALLBACK,
) {
    if (value == null || value === '') return fallback;
    const unit = unitOf(value);
    const range = ranges[unit] || ranges.ch;
    const n = lengthNumber(value);
    if (!Number.isFinite(n) || n <= 0) return fallback;
    const limited = Math.max(range.min, Math.min(range.max, n));
    return storedLength(limited, unit, CONTENT_WIDTH_PREFERRED);
}
